import nprogress from "accessible-nprogress";
import axios from "axios";
import dayjs from "dayjs";
import i18next from "i18next";
import { get } from "lodash";
import React from "react";
import { toast } from "react-toastify";

import { ApiDataConverter } from "./dataConverters";
import DemoModeSvc from "./demoModeSvc";
import { loadingChange } from "../actions/dataAction";
import { Timespan } from "../components/DataDownloadComponents/TimespanPicker/types";
import { FeatureNames } from "../components/EXPERIMENTAL/OrganizationFeatures/types/featureNames";
import { ToolsMetrics } from "../components/InternalTools/IndividualTools/MetricsNew/types";
import { LocationActivityQueryParams } from "../components/LocationActivityComponents/LocationActivityTable/types";
import { API_BASE } from "../config";
import {
  AccountData,
  AccountDataAPI,
  AccountType,
} from "../models/AccountData";
import { CategoryData } from "../models/CategoryData";
import { ChartData } from "../models/ChartCollectionData";
import { CustomDashboardParams } from "../models/CustomDashboardParams";
import { FeatureTypes } from "../models/featureUseLimits/FeatureUseLimitsData.const";
import { GbpCategory } from "../models/GbpCategory";
import { InstagramPost } from "../models/instagram/instagramPost";
import { LabelData } from "../models/LabelData";
import { LocationData } from "../models/LocationData";
import {
  EncodeType,
  LocationDataDownloadParams,
} from "../models/LocationDataDownload";
import { OrgDailyMetricsFromAPIData } from "../models/MetricsLineChartData";
import { MunicipalityData } from "../models/MunicipalityData";
import { OrganizationData } from "../models/OrganizationData";
import {
  LocationStatsHistoricalResponseDTO,
  PeriodType,
} from "../models/OrgHealthChartData";
import {
  OrgRecommendationData,
  RawOrgRecommendationData,
  sanitizeOrgRecommendationData,
} from "../models/OrgRecommendationData";
import { RecMessageDataFromAPI } from "../models/RecMessageData";
import {
  SearchKeywordParams,
  SearchKeywordResponse,
} from "../models/SearchKeywordData";
import {
  GetUserTermsConditionsResponse,
  UpdateTermsConditionsSignatureResponse,
} from "../models/termsConditionsData";
import store from "../store";

export const ERR_TOKEN_IS_NOT_READY = "ERR_TOKEN_IS_NOT_READY";

const axiosInstance = axios.create({
  baseURL: API_BASE,
});

const axiosInstanceNoLoading = axios.create({
  baseURL: API_BASE,
});

const demoMode = DemoModeSvc.current;

axiosInstance.interceptors.request.use(async (config) => {
  if (DataSvc.progressCount === 0) {
    nprogress.configure({ parent: "main" });
    nprogress.start();
    store.dispatch(loadingChange(true));
  }
  DataSvc.progressCount++;

  const token = await DataSvc.getToken();

  // Display the token in the console in development environment
  if (process.env.REACT_APP_SHOW_TOKEN === "true") {
    console.log("token", token);
  }

  if (!token) {
    throw Error(ERR_TOKEN_IS_NOT_READY);
  }

  if (DataSvc.isExpired) {
    throw new Error("expired");
  }

  if (!config.headers) {
    config.headers = {};
  }

  config.headers["Authorization"] = `Bearer ${token}`;
  if (DataSvc.userId) {
    config.headers["AC-User-Id"] = DataSvc.userId;
  }
  if (DataSvc.orgId) {
    config.headers["AC-Org-Id"] = DataSvc.orgId;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    countDownProgress();
    return response;
  },
  async (error) => {
    countDownProgress();

    // Check if the error is due to organization expiry
    if (error.message === "expired") {
      // No toast for organization expired, it will be handled at UI level
      return Promise.reject(error);
    }

    const errorItem = error.response?.data?.error;

    // API passes back error as JSON in OAuth error cases
    let errorJson;
    if (errorItem?.message) {
      if (typeof errorItem.message === "object") {
        errorJson = errorItem.message;
      } else {
        try {
          errorJson = JSON.parse(errorItem.message);
        } catch (e) {
          // Not JSON
        }
      }
    }

    if (
      (error.response?.status === 401 &&
        errorItem?.message === "Unauthorized in Google") ||
      (error.response?.status === 400 &&
        errorJson?.error_description === "Account has been deleted")
    ) {
      // no toast
      return Promise.reject("Unauthorized in Google");
    } else if (
      !error.response?.config?.noHandleError &&
      error.message !== ERR_TOKEN_IS_NOT_READY
    ) {
      const title = get(error, "response.data.error.title", null);

      let errorMessage;

      if (errorJson?.error_description) {
        errorMessage = errorJson?.error_description;
      } else if (errorJson?.translationKey) {
        errorMessage = i18next.t(`server_error.${errorJson.translationKey}`);
      } else if (errorItem?.message) {
        errorMessage = errorItem.message;
      } else {
        errorMessage = i18next.t("errors.general_error");
      }

      toast.error(
        title ? (
          <>
            <strong>{title}</strong>
            <br />
            {errorMessage}
          </>
        ) : (
          <>{errorMessage}</>
        )
      );

      try {
        await DataSvc.createErrorLogs({
          type: "api",
          status_code: error?.response?.status || 500,
          created_by: Number(DataSvc.userId) || null,
          details: {
            url: error?.config?.url,
            method: error?.config?.method,
            title,
            errorMessage,
            errorJson,
            errorItem,
            stack: error?.stack,
            timestamp: new Date().toISOString(),
            userId: DataSvc.userId || null,
            orgId: DataSvc.orgId || null,
            websiteUrl:
              typeof window !== "undefined" ? window.location.href : null,
          },
        });
      } catch (logErr) {
        console.error("Error logging failed:", logErr);
      }
    }
    return Promise.reject(error);
  }
);

axiosInstanceNoLoading.interceptors.request.use(async (config) => {
  if (!DataSvc.getToken()) {
    throw Error(ERR_TOKEN_IS_NOT_READY);
  }

  if (DataSvc.isExpired) {
    throw new Error("expired");
  }

  return DataSvc.getToken().then((token) => {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  });
});

axiosInstanceNoLoading.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check if the error is due to organization expiry
    if (error.message === "expired") {
      // No toast for organization expired, it will be handled at UI level
      return Promise.reject(error);
    }

    const errorItem = error.response?.data?.error;

    // API passes back error as JSON in OAuth error cases
    let errorJson;
    if (errorItem?.message) {
      try {
        errorJson = JSON.parse(errorItem.message);
      } catch (e) {
        // Not JSON
      }
    }

    if (
      (error.response?.status === 401 &&
        errorItem?.message === "Unauthorized in Google") ||
      (error.response?.status === 400 &&
        errorJson?.error_description === "Account has been deleted")
    ) {
      // no toast
      return Promise.reject("Unauthorized in Google");
    } else if (
      !error.response?.config?.noHandleError &&
      error.message !== ERR_TOKEN_IS_NOT_READY &&
      !DataSvc.isExpired
    ) {
      const title = get(error, "response.data.error.title", null);

      let errorMessage;

      if (errorJson?.error_description) {
        errorMessage = errorJson?.error_description;
      } else if (errorItem?.message) {
        errorMessage = errorItem.message;
      } else {
        errorMessage = i18next.t("errors.general_error");
      }

      toast.error(
        title ? (
          <>
            <strong>{title}</strong>
            <br />
            {errorMessage}
          </>
        ) : (
          <>{errorMessage}</>
        )
      );

      try {
        await DataSvc.createErrorLogs({
          type: "api",
          status_code: error?.response?.status || 500,
          created_by: Number(DataSvc.userId) || null,
          details: {
            url: error?.config?.url,
            method: error?.config?.method,
            title,
            errorMessage,
            errorJson,
            errorItem,
            stack: error?.stack,
            timestamp: new Date().toISOString(),
            userId: DataSvc.userId || null,
            orgId: DataSvc.orgId || null,
            websiteUrl:
              typeof window !== "undefined" ? window.location.href : null,
          },
        });
      } catch (logErr) {
        console.error("Error logging failed:", logErr);
      }
    }
    return Promise.reject(error);
  }
);

const countDownProgress = () => {
  DataSvc.progressCount--;
  if (DataSvc.progressCount === 0) {
    nprogress.done();
    store.dispatch(loadingChange(false));
  }
};

declare module "axios" {
  export interface AxiosRequestConfig {
    noHandleError?: boolean;
  }
}

// data service
export default class DataSvc {
  static progressCount = 0;
  static token: Promise<string>;
  static userId?: string;
  static orgId?: string;
  static currentAccount?: AccountData;
  static isExpired = false;

  static isOrganizationExpired(account: AccountData | undefined): boolean {
    if (
      !account ||
      account.type !== AccountType.ORG_ADMIN ||
      !account.organization
    ) {
      return false;
    }

    const expiryDate = account.organization.plan_expiry_date;
    if (!expiryDate) {
      return false;
    }

    const expday = dayjs(account?.organization?.plan_expiry_date)
      .add(-9, "hours")
      .add(1, "days")
      .minute(0)
      .second(0)
      .millisecond(0);
    const today = dayjs();
    return expday.isBefore(today);
  }

  static checkForForcedLogout(): boolean {
    if (
      DataSvc.currentAccount &&
      DataSvc.isOrganizationExpired(DataSvc.currentAccount)
    ) {
      if (!DataSvc.isExpired) {
        DataSvc.isExpired = true;
      }
      return true;
    }
    return false;
  }

  static setToken(token: Promise<string>) {
    DataSvc.token = token;
  }

  static getToken() {
    return DataSvc.token;
  }
  static async getAccountsMe(noLoading?: boolean) {
    const response = await (!noLoading
      ? axiosInstanceNoLoading
      : axiosInstance
    ).get<AccountDataAPI>(`/accounts/me`);
    const userId = response?.data?.id;
    const orgId = response?.data?.organization_id;
    if (userId) {
      DataSvc.userId = userId.toString();
    }
    if (orgId) {
      DataSvc.orgId = orgId;
    }

    const processedAccount = ApiDataConverter.processAccountData(response.data);
    DataSvc.currentAccount = processedAccount;

    return processedAccount;
  }

  static async getAccountByEmail(email: string) {
    const response = await axiosInstance.get<AccountDataAPI[]>(
      `/accounts?email=${encodeURIComponent(email)}`,
      {
        noHandleError: true,
      }
    );
    if (response.data && response.data.length) {
      return ApiDataConverter.processAccountData(response.data[0]);
    }
    return null;
  }

  static async getAccountByAccountId(accountId: string) {
    const response = await axiosInstance.get<AccountDataAPI>(
      `/accounts/${accountId}`
    );

    return ApiDataConverter.processAccountData(response.data);
  }

  static async getAccounts(params: any) {
    const response = await axiosInstance.get<AccountDataAPI[]>(`/accounts`, {
      params,
    });

    return response.data.map(ApiDataConverter.processAccountData);
  }

  static async updateAccount(accountId: string, payload: any) {
    const response = await axiosInstance.patch<AccountDataAPI>(
      `/accounts/${accountId}`,
      payload
    );
    return ApiDataConverter.processAccountData(response.data);
  }

  static async updateRefreshToken(code: string) {
    const response = await axiosInstance.post<AccountDataAPI>(
      `/accounts/updateRefreshToken`,
      { code }
    );
    return ApiDataConverter.processAccountData(response.data);
  }

  static async deleteAccount() {
    const response = await axiosInstance.post(`/accounts/block`);
    return response.data;
  }

  static async findUserAccount(email: string) {
    const response = await axiosInstance.get<AccountData>(
      `/accounts/fullDetails`,
      {
        params: {
          email,
        },
      }
    );
    return response.data;
  }

  static async deleteGoogleLink(email: string) {
    const response = await axiosInstance.delete<{ email: string }>(
      `/accounts/deleteGoogleLink`,
      { data: { email } }
    );
    return response.data;
  }

  static async registerAccount(payload: any) {
    const response = await axiosInstance.post<AccountDataAPI>(
      `/accounts/register`,
      payload
    );
    return ApiDataConverter.processAccountData(response.data);
  }

  static async getCustomDashboards(params: any) {
    const response = await axiosInstanceNoLoading.get<CustomDashboardParams[]>(
      `/custom_dashboards/`,
      params
    );
    return response.data;
  }

  static async getCustomDashboard(dashboardId: any) {
    const response = await axiosInstance.get<CustomDashboardParams>(
      `/custom_dashboards/${dashboardId}`
    );
    return response.data;
  }

  static async createCustomDashboard(payload: any) {
    const response = await axiosInstance.post<CustomDashboardParams>(
      `/custom_dashboards/`,
      payload
    );
    return response.data;
  }

  static async updateCustomDashboard(dashboardId: number, payload: any) {
    const response = await axiosInstance.patch<CustomDashboardParams>(
      `/custom_dashboards/${dashboardId}`,
      payload
    );
    return response.data;
  }

  static async deleteCustomDashboard(dashboardId: number) {
    const response = await axiosInstance.delete(
      `/custom_dashboards/${dashboardId}`
    );
    return response.data;
  }

  static async getLocations(params: any) {
    const { headers, data } = await axiosInstance.get<LocationData[]>(
      `/locations`,
      {
        params,
      }
    );
    const locations = data.map(ApiDataConverter.processLocationData);
    return {
      total: parseInt(headers["x-total-count"]),
      list: locations,
    };
  }

  static async createInternalLabel(params: any) {
    const response = await axiosInstance.post(`/internal-labels`, params);
    return response.data;
  }

  static async updateInternalLabel(params: any, labelId: number) {
    const response = await axiosInstance.patch(
      `/internal-labels/${labelId}`,
      params
    );
    return response.data;
  }

  static async deleteInternalLabel(labelId: number) {
    const response = await axiosInstance.delete(`/internal-labels/${labelId}`);
    return response.data;
  }

  static async getInternalLabels(params: any) {
    const response = await axiosInstance.get(`/internal-labels`, {
      params,
    });
    return response.data;
  }

  static async batchOperationInternalLabels(params: any) {
    const response = await axiosInstance.post(
      `/internal-labels/batch-operate`,
      params
    );
    return response.data;
  }

  static async addLocationInternalLabel(
    locationId: number,
    interlLabelId: number
  ) {
    const response = await axiosInstance.post(
      `/internal-labels/${interlLabelId}/location/${locationId}`
    );
    return response.data;
  }

  static async deleteLocationInternalLabel(
    locationId: number,
    interlLabelId: number
  ) {
    const response = await axiosInstance.delete(
      `/internal-labels/${interlLabelId}/location/${locationId}`
    );
    return response.data;
  }

  static async getLabels(params: any, fetchWithoutBlock: boolean) {
    const axios = fetchWithoutBlock ? axiosInstanceNoLoading : axiosInstance;
    const response = await axios.get<LabelData[]>(`/labels`, {
      params,
    });
    return response.data;
  }

  static async deleteLocationLabel(locationId: number, labelId: number) {
    const response = await axiosInstance.delete(
      `/locations/${locationId}/labels/${labelId}`
    );
    return response.data;
  }

  static async addLocationLabel(locationId: number, labelId: number) {
    const response = await axiosInstance.put(
      `/locations/${locationId}/labels/${labelId}`
    );
    return response.data;
  }

  static async sendEmailToLocation(payload: any) {
    await axiosInstance.post(
      "/organizationsJobs/send_recommendation_mail",
      payload
    );
  }

  static async createLabel(label: any) {
    const response = await axiosInstance.post(`/labels`, label);
    return response.data;
  }

  static async updateLabel(labelId: number, newText: string) {
    const response = await axiosInstance.patch(`/labels/${labelId}`, {
      name: newText,
    });
    return response.data;
  }

  static async deleteLabel(labelId: number) {
    const response = await axiosInstance.delete(`/labels/${labelId}`);
    return response.data;
  }

  static async getLocation(locationId: string) {
    const response = await axiosInstance.get<LocationData>(
      `/locations/${locationId}`
    );
    return ApiDataConverter.processLocationData(response.data);
  }

  static async getRecommendations(params: any, noLoading: boolean) {
    const { data } = await (noLoading
      ? axiosInstanceNoLoading
      : axiosInstance
    ).get<(RawOrgRecommendationData | OrgRecommendationData)[]>(
      `/recommendations`,
      {
        params,
      }
    );

    let recommendations = data.map(sanitizeOrgRecommendationData);
    if (demoMode.isDemoModeOn) {
      recommendations = recommendations.map((rec) => ({
        ...rec,
        ...(!params.organization_id && {
          title: "SNS投稿、商品情報でデモ町を盛り上げよう！",
          body: "直近90日の投稿が少ないようです。デモ町では夏祭りも開催されます。地元の夏祭り情報と合わせたおすすめ商品情報を発信して、観光客と地元住民の両方にアピールしましょう！",
        }),
        relevant_locations: rec.relevant_locations?.map(
          demoMode.processSummaryLocation
        ),
      }));
    }
    return recommendations;
  }

  static async getCustomOrgRecommendations(params: any, noLoading: boolean) {
    const { data } = await (noLoading
      ? axiosInstanceNoLoading
      : axiosInstance
    ).get<OrgRecommendationData[]>(`/recommendations/custom`, {
      params,
    });
    return demoMode.isDemoModeOn
      ? data.map((rec) => ({
          ...rec,
          relevant_locations: rec.relevant_locations?.map(
            demoMode.processSummaryLocation
          ),
        }))
      : data;
  }

  static async getStatsPrime(organizationId: string) {
    const response = await axiosInstanceNoLoading.get(`/stats/prime`, {
      params: { organization_id: organizationId },
    });
    return response.data;
  }

  static async getStatsCustomPrime(
    organizationId: string,
    customDashboardId: string
  ) {
    const response = await axiosInstanceNoLoading.get(`/stats/prime/custom`, {
      params: {
        organization_id: organizationId,
        custom_dashboard_id: customDashboardId,
      },
    });
    return response.data;
  }

  static async getStatsStatus(
    organizationId: string,
    customDashboardParams?: CustomDashboardParams
  ) {
    const response = await axiosInstanceNoLoading.get(`/stats/status`, {
      params: {
        organization_id: organizationId,
        ...(customDashboardParams
          ? { ...customDashboardParams.conditions }
          : {}),
      },
    });
    return response.data;
  }

  static async getLocationStatsHistorical(params: {
    organization_id: number;
    start_date: Date;
    end_date: Date;
    period: PeriodType;
  }) {
    const { organization_id, start_date, end_date, period } = params;

    const formattedStartDate =
      start_date instanceof Date ? start_date.toISOString() : start_date;
    const formattedEndDate =
      end_date instanceof Date ? end_date.toISOString() : end_date;

    const response =
      await axiosInstanceNoLoading.get<LocationStatsHistoricalResponseDTO>(
        `/metrics/location-historical`,
        {
          params: {
            organization_id,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            period,
          },
        }
      );
    return response.data;
  }

  static async getLocationStatsHistoricalCsv(params: {
    organization_id: number;
    start_date: Date;
    end_date: Date;
  }) {
    const response = await axiosInstance.get(
      `/metrics/location-historical-csv`,
      { params }
    );
    return response.data;
  }

  /**
   * This function does not use Axios because we want the request to be fired once and forgotten.
   * We want to avoid any potential side effects from Axios (retries, logging / displaying errors, etc).
   */
  static async createErrorLogs(payload: any) {
    try {
      // If there is no access token, abandon the request
      const token = await DataSvc.getToken();
      if (!token) {
        return;
      }

      const url = `${API_BASE}/error-logs`;

      // Do nothing with the response
      await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      // Ignore failure
    }
  }

  static async getErrorLogs(params: any) {
    const response = await axiosInstance.get("/error-logs", { params });

    return response.data;
  }

  // start new

  static async getChartCollections(params: any) {
    const response = await axiosInstance.get("/chartCollections", { params });

    return response.data;
  }

  static async createCollection(payload: any) {
    const response = await axiosInstance.post(`/chartCollections`, payload);
    return response.data;
  }

  static async updateCollection(collectionId: string, payload: any) {
    const response = await axiosInstance.patch(
      `/chartCollections/${collectionId}`,
      payload
    );
    return response.data;
  }

  static async getChartCollectionsByCollectionId(
    collectionId: string | undefined
  ) {
    if (!collectionId) {
      return [];
    }
    const response = await axiosInstance.get<ChartData[]>(
      `/chartCollections/${collectionId}/charts`
    );

    return response.data;
  }

  static async getChartDataByTimeSeries(params: any) {
    // avoid sending unnecessary parameters.
    DataSvc.removePropertiesIfNoValue(params);
    const response = await axiosInstance.get(`/metrics/time_series`, {
      params,
    });

    return response.data;
  }

  static async getChartDataByGroupAggregations(params: any) {
    // avoid sending unnecessary parameters.
    DataSvc.removePropertiesIfNoValue(params);
    const response = await axiosInstance.get(`/metrics/group_aggregations`, {
      params,
    });

    return response.data;
  }

  static async createChartByTemplate(collectionId: string, params: any) {
    const response = await axiosInstance.post(
      `/chartCollections/${collectionId}/charts/createByTemplate`,
      {},
      {
        params,
      }
    );

    return response.data;
  }

  static async updateChart(
    collectionId: string,
    chartId: string,
    payload: any
  ) {
    // DataSvc.removePropertyIfNoValue(payload, 'municipality_filter');
    // DataSvc.removePropertyIfNoValue(payload, 'category_filter');
    // DataSvc.removePropertyIfNoValue(payload, 'label_filter');
    const response = await axiosInstance.patch(
      `/chartCollections/${collectionId}/charts/${chartId}`,
      payload
    );

    return response.data;
  }

  static async deleteChart(collectionId: string, chartId: string) {
    const response = await axiosInstance.delete(
      `/chartCollections/${collectionId}/charts/${chartId}`
    );
    return response.data;
  }

  static async getChartById(collectionId: string, chartId: string) {
    const response = await axiosInstance.get(
      `/chartCollections/${collectionId}/charts/${chartId}`
    );
    return response.data;
  }

  // end new

  static async getMetrics(params: any) {
    const response =
      await axiosInstanceNoLoading.get<OrgDailyMetricsFromAPIData>(`/metrics`, {
        params,
      });
    return response.data;
  }

  static async getLocationEvents(params: any) {
    const response =
      await axiosInstanceNoLoading.get<OrgDailyMetricsFromAPIData>(
        `/metrics/location_events`,
        { params }
      );
    return response.data;
  }

  static async getCategories(fetchWithoutBlock: boolean) {
    const axios = fetchWithoutBlock ? axiosInstanceNoLoading : axiosInstance;
    const response = await axios.get<CategoryData[]>("/categories");
    return response.data;
  }

  static async getGbpCategories() {
    const axios = axiosInstanceNoLoading;
    const response = await axios.get<GbpCategory[]>(
      "/categories/gbp_categories"
    );
    return response.data;
  }

  static async getMunicipalities(organizationId: string) {
    const response = await axiosInstance.get<MunicipalityData[]>(
      "/municipalities",
      {
        params: {
          organization_id: organizationId,
          limit: 300,
        },
      }
    );
    return response.data;
  }

  static async getMunicipalitiesOfLocations(
    organizationId: string,
    fetchWithoutBlock: boolean
  ) {
    const axios = fetchWithoutBlock ? axiosInstanceNoLoading : axiosInstance;
    const response = await axios.get<MunicipalityData[]>(
      "/municipalities/locationMunis",
      {
        params: {
          organization_id: organizationId,
        },
      }
    );
    return response.data;
  }

  static async getMunicipalitiesOfUser(
    userId: number,
    fetchWithoutBlock: boolean
  ) {
    const axios = fetchWithoutBlock ? axiosInstanceNoLoading : axiosInstance;
    const response = await axios.get<MunicipalityData[]>(
      "/municipalities/locationMunisForUser",
      {
        params: {
          user_id: userId,
        },
      }
    );
    return response.data;
  }

  static async getMunicipalitiesParent() {
    const response = await axiosInstance.get(`/municipalities`, {
      params: {
        parent_only: true,
        limit: 100,
        sort: "external_id",
      },
    });
    return response.data;
  }

  static async getMunicipalitiesChild(external_id: string) {
    const response = await axiosInstance.get<MunicipalityData[]>(
      `/municipalities`,
      {
        params: {
          parent_id: external_id,
          limit: 300,
          sort: "external_id",
        },
      }
    );
    return response.data;
  }

  static async getOrganizations(params: any) {
    const response = await axiosInstance.get<OrganizationData[]>(
      `/organizations`,
      {
        params,
      }
    );
    return {
      total: parseInt(response.headers["x-total-count"]),
      list: response.data.map(ApiDataConverter.processOrganizationData),
    };
  }

  static async getOrganization(params: any) {
    const response = await axiosInstance.get<OrganizationData>(
      `/organizations/${params.organizationId}`
    );
    return ApiDataConverter.processOrganizationData(response.data);
  }

  static async getRanking(path: string, params: any) {
    const { data } = await axiosInstanceNoLoading.get(`/ranking/${path}`, {
      params,
    });
    if (path === "locations" && demoMode.isDemoModeOn) {
      return {
        ...data,
        ranking: data.ranking.map(demoMode.processLocation),
      };
    }
    return data;
  }

  static async getRecommendationsHistory(
    organization_id: string,
    size: number,
    noLoading: boolean
  ) {
    const response = await (noLoading
      ? axiosInstanceNoLoading
      : axiosInstance
    ).get(`/recommendations/history`, {
      params: {
        organization_id,
        size,
      },
    });

    return response.data;
  }

  static async getLocationRecommendationHistory(
    organization_id: string,
    location_id: string,
    size: number
  ) {
    const response = await axiosInstance.get(`/recommendations/history`, {
      params: {
        ...(organization_id && { organization_id }),
        location_id,
        size,
      },
    });

    return response.data;
  }

  static async getRecMessages(organization_id: string) {
    const { data } = await axiosInstance.get<RecMessageDataFromAPI[]>(
      `/org_recommendation_records`,
      {
        params: {
          organization_id,
        },
      }
    );

    const recData = demoMode.isDemoModeOn
      ? data.map((rec) => ({
          ...rec,
          relevant_locations: rec.relevant_locations.map(
            demoMode.processSummaryLocation
          ),
        }))
      : data;

    return recData;
  }

  static async getManagementLocations() {
    const { data } = await axiosInstance.post<LocationData[]>(
      "/management/locations/import"
    );
    return data.map(ApiDataConverter.processLocationData);
  }

  static async registerLocation(locationId: number) {
    const response = await axiosInstance.post(
      `/management/locations/${locationId}/register`
    );

    return response.data;
  }

  static async deregisterLocation(locationId: number) {
    const response = await axiosInstance.post(
      `/management/locations/${locationId}/deregister`
    );

    return response.data;
  }

  static async deleteLocation(locationId: number) {
    const response = await axiosInstance.delete(`/locations/${locationId}`);
    return response.data;
  }

  static async deleteUseLimits(target_id: number, featureType: FeatureTypes) {
    const response = await axiosInstance.delete(
      `/featureUseLimit/${featureType}_use_limits/${target_id}`
    );
    return response;
  }

  static async registerLocationWithOrg(locationId: number, orgId: number) {
    const response = await axiosInstance.put(
      `/locations/${locationId}/organizations/${orgId}`
    );
    return response.data;
  }

  static async deregisterLocationWithOrg(locationId: number, orgId: number) {
    const response = await axiosInstance.delete(
      `/locations/${locationId}/organizations/${orgId}`
    );
    return response.data;
  }

  static async createOrganization(payload: any) {
    const response = await axiosInstance.post("/organizations", payload);
    return response.data;
  }

  static async addMunicipalityToOrganization(
    organization_id: number,
    municipality_id: number
  ) {
    const response = await axiosInstance.put(
      `/organizations/${organization_id}/municipalities/${municipality_id}`
    );
    return response.data;
  }

  static async updateOrganization(organization_id: number, payload: any) {
    const response = await axiosInstance.patch(
      `/organizations/${organization_id}`,
      payload
    );
    return response.data;
  }

  static async updateLocations(location_id: number, payload: any) {
    const response = await axiosInstance.patch(
      `/locations/${location_id}`,
      payload
    );
    return response.data;
  }

  static async removeMunicipalityFromOrganization(
    organization_id: number,
    municipality_id: number
  ) {
    const response = await axiosInstance.delete(
      `/organizations/${organization_id}/municipalities/${municipality_id}`
    );
    return response.data;
  }

  static async removeOrganization(organization_id: string) {
    const response = await axiosInstance.delete(
      `/organizations/${organization_id}`
    );
    return response.data;
  }

  static async getOrgsLoginRate() {
    const response = await axiosInstance.get(`/organizations/orgs_login_rate`);
    return response.data;
  }

  static removePropertyIfNoValue(obj: any, propName: string) {
    if (typeof obj !== "object" || !propName) {
      return;
    }

    if (
      typeof obj[propName] === "undefined" ||
      obj[propName] === null ||
      obj[propName] === ""
    ) {
      delete obj[propName];
    }
    if (Array.isArray(obj[propName])) {
      const arr = obj[propName] as [];
      const concatArr = arr
        .map((it) => {
          return it ? it : "";
        })
        .join();
      if (concatArr.length === 0) {
        delete obj[propName];
      }
    }
  }

  static removePropertiesIfNoValue(obj: any) {
    if (typeof obj !== "object") {
      return;
    }
    for (const p in obj) {
      DataSvc.removePropertyIfNoValue(obj, p);
    }
  }

  static async getProfileCompletionLeaderboard() {
    const response = await axiosInstance.get("/ranking/org_prof_compl");

    return response.data;
  }

  // Internal tools

  static async getPlaceScores(formData: FormData) {
    const response = await axiosInstance.post(
      `/tools/get_review_scores`,
      formData
    );
    return response.data;
  }

  static async getFeatures(data: any) {
    const response = await axiosInstance.get(`/featureUseLimit/features`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getUseLimits(data: any) {
    const response = await axiosInstance.get(`/featureUseLimit/use_limits`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getUseLimitsForTarget(
    id: number | string,
    featureType: FeatureTypes
  ) {
    const response = await axiosInstance.get(
      `/featureUseLimit/${featureType}_use_limits/${id}`
    );
    return response.data;
  }

  static async updateUseLimit(payload: any, featureType: FeatureTypes) {
    const response = await axiosInstance.patch(
      `/featureUseLimit/${featureType}_use_limit`,
      payload
    );
    return response.data;
  }

  static async getReviews(data: any) {
    const response = await axiosInstance.get(`/tools/get_reviews`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getFacilityReviews(data: any) {
    const response = await axiosInstance.get("/reviews", {
      params: data,
    });

    const reviews = response?.data.data || [];
    const count = response?.data.count || 0;

    const processedReviews = reviews.map(ApiDataConverter.processReview);
    return {
      data: processedReviews,
      count,
    };
  }

  static async postReplyReview(data: any) {
    const response = await axiosInstance.post("/reviews/reply", data);
    return response.data;
  }

  static async getProcessedReviews(location_id: string) {
    const response = await axiosInstance.post("/reviews/processed-reviews", {
      location_id,
    });
    return response.data;
  }

  static async getReviewsForLocation(params: {
    locationId: number;
    skipNum?: number;
    nextSetToken?: string;
  }) {
    const response = await axiosInstance.get(
      `/tools/get_reviews_for_location`,
      {
        params,
      }
    );
    return response.data;
  }

  static async generateCustomReviewSummaries(data: any) {
    const response = await axiosInstance.post(
      "/review_summaries/generate_custom_location_review_summaries",
      data
    );
    return response.data;
  }

  static async deleteCustomReviewSummary(id: number) {
    const response = await axiosInstance.delete(`/review_summaries/${id}`);
    return response.data;
  }

  static async checkOwnerArea(data: any) {
    const response = await axiosInstance.post(`tools/check_owner_area`, data);

    return response.data;
  }

  static async checkOwnerList(formData: FormData) {
    const response = await axiosInstance.post(
      `tools/check_owner_list`,
      formData
    );

    return response.data;
  }

  static async checkOwner(data: any) {
    const response = await axiosInstance.get(`/tools/check_owner`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getToolsMetrics(data: {
    locationName: string;
    dataStartDate: string;
    dataEndDate: string;
    useWeekly: boolean;
    comparisonStartDate: string;
    comparisonEndDate: string;
    comparisonTargetStartDate: string;
    comparisonTargetEndDate: string;
    skipNum: number | null;
  }): Promise<ToolsMetrics> {
    const response = await axiosInstance.get(`/tools/metrics`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getToolsMetricsForLocation(params: {
    locationId: number;
    dataStartDate: string;
    dataEndDate: string;
    useWeekly: boolean;
    comparisonStartDate: string;
    comparisonEndDate: string;
    comparisonTargetStartDate: string;
    comparisonTargetEndDate: string;
    skipNum: number | null;
  }): Promise<ToolsMetrics> {
    const response = await axiosInstance.get(`/tools/metrics_for_location`, {
      params,
    });
    return response.data;
  }

  static async getToolsAreaMetrics(data: any) {
    const response = await axiosInstance.get(`/tools/areaMetrics`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async getToolsAreaMetricsForOrg(data: any) {
    const response = await axiosInstance.get(`/tools/areaMetrics_for_org`, {
      params: {
        ...data,
      },
    });
    return response.data;
  }

  static async sendMassEmail(payload: any) {
    await axiosInstance.post("/admin/mass_send_email", payload);
  }

  static async getMedia(params: any) {
    const response = await axiosInstance.get(`/media`, { params });

    const media = response.data.map(ApiDataConverter.processMedia);

    return media;
  }

  static async createLocalPost(locationId: string, formData: FormData) {
    const response = await axiosInstance.post(
      `/locations/${locationId}/createLocalPost`,
      formData
    );
    return response;
  }

  static async createSchedulePost(locationId: string, formData: FormData) {
    const response = await axiosInstance.post(
      `/locations/${locationId}/createSchedulePost`,
      formData
    );
    return response;
  }

  static async cancelSchedulePost(locationId: string, schedulePostId: string) {
    const response = await axiosInstance.delete(
      `/locations/${locationId}/schedulePost/${schedulePostId}`
    );
    return response.data;
  }

  static async deleteLocalPost(locationId: string, postId: string) {
    const response = await axiosInstance.delete(
      `/local_posts/location/${locationId}/post/${postId}`
    );
    return response.data;
  }

  static async updateLocalPost(
    locationId: string,
    postId: string,
    formData: FormData
  ) {
    const response = await axiosInstance.patch(
      `/local_posts/location/${locationId}/post/${postId}`,
      formData
    );
    return response.data;
  }

  static async generateLocalPostTextWithGemini(data: any) {
    const response = await axiosInstance.post(
      `/gemini/generate_loc_post_text/`,
      data
    );
    return response.data as string;
  }

  static async generateGbpCategorySuggestions(
    locationId: number
  ): Promise<GbpCategory[]> {
    const response = await axiosInstance.post(
      `/gemini/generate_gbp_category_suggestions/locations/${locationId}`
    );
    return response.data as GbpCategory[];
  }

  static async getLocalPosts(params: any) {
    const response = await axiosInstance.get(`/local_posts`, { params });

    const localPosts = response.data.data.map(
      ApiDataConverter.processLocalPostData
    );

    return { data: localPosts, count: response.data.count };
  }

  static async getLocalPostIdeas(location_id: string) {
    const response = await axiosInstance.get(
      `/local_posts/ideas/${location_id}`
    );
    return response.data;
  }

  static async authorizeInstagram(payload: any) {
    const response = await axiosInstance.post(`/instagram/authorize`, payload);
    return response.data;
  }

  static async deleteInstagramIntegration(
    location_id: string,
    user_id: string
  ) {
    const response = await axiosInstance.delete(
      `instagram/remove_integration/location/${location_id}/user/${user_id}`
    );
    return response.data;
  }

  static async massPostLocalPosts(formData: FormData) {
    const response = await axiosInstance.post(
      `/local_posts/mass_post`,
      formData
    );
    return response.data;
  }

  static async getLatestLocationReviewSummary(locationId: number) {
    const response = await axiosInstance.get(
      `/review_summaries/locations/${locationId}/latest`
    );
    return response.data;
  }

  static async getReviewSummaries(params: any) {
    const response = await axiosInstance.get(`/review_summaries`, {
      params,
    });

    return response.data;
  }

  static async getLocationReviewSummaryHistory(
    locationId: number,
    params: any
  ) {
    const response = await axiosInstance.get(
      `/review_summaries/locations/${locationId}/history`,
      {
        params,
      }
    );
    return response.data;
  }

  static async getLocationReviewSummary(
    locationId: number,
    id?: number,
    params?: any
  ) {
    if (!id) {
      return null;
    }
    const response = await axiosInstance.get(
      `/review_summaries/locations/${locationId}/location_review_summaries/${id}`,
      {
        params,
      }
    );

    const summary = ApiDataConverter.processLocationReviewSummary(
      response.data
    );

    return summary;
  }

  static async getLocationReviewSummaryReviews(locationId: number, id: number) {
    const response = await axiosInstance.get(
      `/review_summaries/locations/${locationId}/location_review_summaries/${id}/reviews`
    );
    return response.data;
  }

  static async updateLocationProfile(locationId: number, payload: any) {
    const response = await axiosInstance.patch(
      `/locations/${locationId}/profile`,
      payload
    );
    return response.data;
  }

  /**
   * To be used by the AIGenerateButton component. When creating endpoints for this, ensure they are on the Gemini Controller.
   */
  static async generateAiText(url: string, data?: any) {
    const response = await axiosInstance.post(`/gemini/${url}`, data);
    return response.data;
  }

  static async updateLocationSocialProfiles(locationId: number, payload: any) {
    const response = await axiosInstance.patch(
      `/locations/${locationId}/social_links`,
      payload
    );
    return response.data;
  }

  static async getLocationPreferences(locationId: number) {
    const response = await axiosInstance.get(
      `/location-preferences/${locationId}`
    );
    return response.data;
  }

  static async updateLocationPreferences(locationId: number, payload: any) {
    const response = await axiosInstance.patch(
      `/location-preferences/${locationId}`,
      payload
    );
    return response.data;
  }

  static async getLocationsActivity(params: LocationActivityQueryParams) {
    const response = await axiosInstance.get(`/location-activity-logs`, {
      params,
    });

    const activityLogs = ApiDataConverter.processActivityLogs(
      response.data.data
    );

    return { data: activityLogs, count: response.data.count };
  }

  static async getLogDetail(locationId: string, logId: string) {
    const response = await axiosInstance.get(
      `/location-activity-logs/location/${locationId}/activity-log/${logId}`
    );
    return response.data;
  }

  static async getAdminLocationAlerts(params: any) {
    const response = await axiosInstance.get(`/admin-location-alerts`, {
      params,
    });
    return response.data;
  }

  static async createAdminLocationAlert(payload: any) {
    const response = await axiosInstance.post(
      `/admin-location-alerts`,
      payload
    );
    return response.data;
  }

  static async deleteAdminLocationAlert(id: string) {
    const response = await axiosInstance.delete(`/admin-location-alerts/${id}`);
    return response.data;
  }

  static async updateAdminLocationAlert(payload: any) {
    const response = await axiosInstance.put(
      `/admin-location-alerts/${payload.id}`,
      payload
    );
    return response.data;
  }

  static async getSearchKeyword(params?: SearchKeywordParams) {
    const response = await axiosInstance.get<SearchKeywordResponse>(
      `/search-keywords`,
      {
        params,
      }
    );

    return response.data;
  }

  static async getLocationInstagramPosts(
    locationId: string,
    params: any
  ): Promise<{ data: InstagramPost[]; count: number }> {
    const response = await axiosInstance.get(
      `/instagram/location/${locationId}/posts`,
      {
        params,
      }
    );
    return response.data;
  }

  static async getScheduledPosts(locationId: string, params: any) {
    const response = await axiosInstance.get(
      `/locations/${locationId}/schedulePosts`,
      { params }
    );
    return response.data;
  }

  static async getUserTermsConditions(
    accountId: string
  ): Promise<GetUserTermsConditionsResponse> {
    const response = await axiosInstance.get<GetUserTermsConditionsResponse>(
      `/terms-conditions/account/${accountId}`
    );
    return response.data;
  }

  static async updateTermsConditionsSignature(
    accountId: string,
    termsConditionId: string,
    param: any
  ): Promise<UpdateTermsConditionsSignatureResponse> {
    const response =
      await axiosInstance.patch<UpdateTermsConditionsSignatureResponse>(
        `/terms-conditions/${termsConditionId}/account/${accountId}`,
        param
      );
    return response.data;
  }

  static async getFacilityDataMetrics({
    organizationId,
    timespan,
    timeUnit,
    locations,
  }: {
    organizationId: string;
    timespan: Timespan;
    timeUnit: "day" | "week" | "month";
    locations: LocationData[];
  }): Promise<string> {
    // This is not yet implemented in the backend, and might not be the final URL.
    const data = await axiosInstance.post(
      `/metrics/organization/${organizationId}/csv`,
      {
        location_ids: locations.map((location) => location.id),
        start_date: timespan.startDate?.format("YYYY-MM-DD"),
        end_date: timespan.endDate?.format("YYYY-MM-DD"),
        period: timeUnit,
      }
    );
    return data.data;
  }

  static async getFacilityDataActivityLogs({
    organizationId,
    timespan,
    locations,
  }: {
    organizationId: string;
    timespan: Timespan;
    locations: LocationData[];
  }) {
    const data = await axiosInstance.post(
      `/location-activity-logs/organization/${organizationId}/csv`,
      {
        location_ids: locations.map((location) => location.id),
        start_date: timespan.startDate?.format("YYYY-MM-DD"),
        end_date: timespan.endDate?.format("YYYY-MM-DD"),
      }
    );
    return data.data;
  }

  static async EXPERIMENTAL_getOrganizationFeatures({
    featureName,
    organizationId,
  }: {
    featureName: FeatureNames;
    organizationId: string;
  }) {
    const response = await axiosInstance.get(
      `/EXPERIMENTAL/organization-features/${featureName}/organization/${organizationId}`
    );
    return response.data;
  }

  static async EXPERIMENTAL_generateOrganizationFeatureData({
    featureName,
    organizationId,
  }: {
    featureName: FeatureNames;
    organizationId: string;
  }) {
    const response = await axiosInstance.post(
      `/EXPERIMENTAL/organization-features/${featureName}/organization/${organizationId}`
    );
    return response.data;
  }

  static async downloadLocationData(params: LocationDataDownloadParams) {
    const response = await axiosInstance.post(
      `/locations/download`,
      {
        location_id: Number(params.location_id),
        data_type: params.data_type,
        encode: params.encode || EncodeType.UTF8,
      },
      {
        responseType: "blob",
      }
    );
    return response.data;
  }

  // FAQ API methods
  static async getAllFaq() {
    const response = await axiosInstance.get(`/faqs`);
    return response.data;
  }

  static async createFaq(params: any) {
    const response = await axiosInstance.post(`/faqs`, params);
    return response.data;
  }

  static async deleteFaq(
    faqId: number
  ): Promise<{ success: boolean; message?: string }> {
    const response = await axiosInstance.delete(`/faqs/${faqId}`);
    return response.data;
  }

  static async getProjects(params: { organizationId: string }) {
    const response = await axiosInstance.get(
      `/projects/organization/${params.organizationId}`
    );
    return response.data;
  }

  static async getProjectsLocations({
    organizationId,
    projectId,
    params,
  }: {
    organizationId: string;
    projectId?: string;
    params: any;
  }) {
    if (!projectId) {
      return;
    }
    const response = await axiosInstance.get(
      `/projects/organization/${organizationId}/project/${projectId}/locations`,
      {
        params,
      }
    );
    return response.data;
  }

  static async updateProject(params: {
    organizationId: string;
    projectId: string;
    payload: any;
  }) {
    const response = await axiosInstance.patch(
      `/projects/organization/${params.organizationId}/project/${params.projectId}`,
      params.payload
    );
    return response.data;
  }

  static async updateProjectLocation(params: {
    organizationId: string;
    projectId?: string;
    projectLocationId?: string;
    payload: any;
  }) {
    if (!params.projectId || !params.projectLocationId) {
      return;
    }
    const response = await axiosInstance.patch(
      `/projects/organization/${params.organizationId}/project/${params.projectId}/project-location/${params.projectLocationId}`,
      params.payload
    );
    return response.data;
  }
}
